import { UsersRatings } from "../UsersRatings/UsersRatings";
import { StarRating } from "../StarRating/StarRating";
import { Button, Divider, Message, Modal } from "../../../BP2Components";
import { useIntl } from "react-intl";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { FileOverlay } from "../FileOverlay/FileOverlay";
import { getLabelFromTechName } from "../../../FormsV2/FormFields/QuestionnaireTableField/questionnarieTableScripts";
import { useFormContext } from "../../../../context/FormContext/FormContext";
import FormComplete from "../../../FormsV2/FormComplete";
import { motion, AnimatePresence } from "framer-motion";
import { useSelector } from "react-redux";

export type OpinionResponse = {
   data: {
      default: {
         columns: Array<{ tech_name: string; label: string }>;
         data: Array<Record<string, any>>;
         meta: { addItemFormId: string };
      };
   };
} | null;

interface OpinionsTabProps {
   opinionResponse: OpinionResponse;
   rates: number[];
   averageRate: number;
   kli_id_list: string;
   loadOpinions: () => void;
}

export const OpinionsTab = ({
   opinionResponse,
   rates,
   averageRate,
   kli_id_list,
   loadOpinions,
}: OpinionsTabProps) => {
   if (!opinionResponse) return null;

   const {
      columns,
      data: opinions,
      meta: { addItemFormId },
   } = opinionResponse.data.default;

   const [selectedFile, setSelectedFile] = useState<string | null>(null);
   const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
   const { form } = useFormContext();
   const intl = useIntl();
   const { authUser } = useSelector(({ auth }) => auth);

   const closeOverlay = () => setSelectedFile(null);
   const handleImageClick = (fileUrl: string) => setSelectedFile(fileUrl);
   const handleAddOpinion = () => setIsFormOpen(true);

   const handleSaveCallback = () => {
      Message.destroy();
      Message.success(intl.formatMessage({ id: "addRecordBtn.newOptionHasBeenAdded" }), 5000);
      setIsFormOpen(false);
      loadOpinions();
   };

   useEffect(() => {
      if (isFormOpen && form) {
         const currentValue = form.getFieldValue("Ratings_rated_object_id_fqvjx8");
         if (JSON.stringify(currentValue) !== JSON.stringify(kli_id_list)) {
            form.setFieldsValue({
               Ratings_rated_object_id_fqvjx8: kli_id_list,
            });
         }
      }
   }, [isFormOpen, form, kli_id_list]);

   return (
      <div className="business-card__opinions">
         {rates.length > 0 ? (
            <div className="business-card__user-rate-container">
               <UsersRatings rates={rates} />
               <StarRating type="column" rate={averageRate} numberOfOpinions={rates.length} />
            </div>
         ) : null}

         {typeof authUser !== "number" && (
            <Button className="business-card__write-opinion" onClick={handleAddOpinion}>
               {intl.formatMessage({ id: "business.addOpinion" })}
            </Button>
         )}

         {rates.length > 0 ? <Divider className="business-card__divider" /> : null}

         {opinions.length === 0 ? (
            <div className="business-card__no-opinions">
               <h3 className="business-card__section-title">
                  {intl.formatMessage({ id: "business.noOpinions" })}
               </h3>
            </div>
         ) : (
            <AnimatePresence>
               {opinions.map((singleOpinion) => {
                  const id = singleOpinion?.[getLabelFromTechName("Ratings_id_7udtuo", columns)];
                  const author =
                     singleOpinion?.[getLabelFromTechName("Ratings_user_id_cv3b6l", columns)];
                  const date =
                     singleOpinion?.[getLabelFromTechName("Ratings_ssr_updated_13ude4", columns)];
                  const images = singleOpinion?.[getLabelFromTechName("zdjecie", columns)];
                  const comment =
                     singleOpinion?.[getLabelFromTechName("Ratings_comment_nb1jnz", columns)];
                  const rate =
                     singleOpinion?.[getLabelFromTechName("Ratings_rating_g60fqz", columns)];

                  return (
                     <motion.div
                        key={id}
                        className="business-card__opinion"
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.4, ease: "easeOut" }}
                     >
                        <div className="business-card__opinion-user">
                           <div>
                              <p className="business-card__opinion-username">
                                 <span>{author?.value || author}</span>
                                 <span className="business-card__opinion-date">
                                    {format(new Date(date), "d MMMM yyyy")}
                                 </span>
                              </p>
                              <p className="business-card__opinion-user-rating">
                                 <StarRating type="userInfo" rate={rate?.value || rate} />
                              </p>
                           </div>
                        </div>

                        {!!images.length && (
                           <div className="business-card__gallery">
                              {images.map(({ fileUrl }) => (
                                 <img
                                    src={fileUrl}
                                    alt="Gallery"
                                    className="business-card__gallery-item business-card__gallery-item--opinions"
                                    onClick={() => handleImageClick(fileUrl)}
                                    key={fileUrl}
                                 />
                              ))}
                           </div>
                        )}

                        <p className="business-card__opinion-text">{comment}</p>
                     </motion.div>
                  );
               })}
            </AnimatePresence>
         )}
         {selectedFile && <FileOverlay file={selectedFile} onClose={closeOverlay} />}
         <Modal isOpen={isFormOpen} onCancel={() => setIsFormOpen(false)}>
            <FormComplete
               formId={Number(addItemFormId)}
               form={form}
               saveCallback={handleSaveCallback}
               attachmentLabelText={intl.formatMessage({ id: "sidebar.right.PhotosCard" })}
            />
         </Modal>
      </div>
   );
};
